/* LG EXPRESS — Motion Kernel (loaded globally via lg-base snippet)
   - Fade-up reveal          [data-lg-fade]  (+ data-lg-fade-idx for stagger)
   - Blur-up on images       [data-lg-blur-up]
   - Scroll parallax         [data-lg-parallax-root] > [data-lg-parallax-el data-lg-parallax-speed]
   - Hero scroll fade        [data-lg-hero]
   - Magnetic hover buttons  [data-lg-magnetic]
   - Sticky header shadow    #header-group
   - Animated counters       [data-lg-count]
*/
(function () {
  'use strict';

  const reduceMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ===== FADE-UP ===== */
  function initFades() {
    if (!('IntersectionObserver' in window)) return;
    const els = document.querySelectorAll('[data-lg-fade]:not([data-lg-in])');
    if (!els.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const idx = parseInt(e.target.getAttribute('data-lg-fade-idx') || '0', 10);
        e.target.style.transitionDelay = `${Math.min(idx, 20) * 65}ms`;
        e.target.setAttribute('data-lg-in', 'true');
        io.unobserve(e.target);
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -6% 0px' });
    els.forEach((el) => io.observe(el));
  }

  /* ===== BLUR-UP IMAGES ===== */
  function initBlurUp() {
    if (!('IntersectionObserver' in window)) return;
    const els = document.querySelectorAll('[data-lg-blur-up]:not([data-lg-in])');
    if (!els.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const target = e.target;
        if (target.tagName === 'IMG' && !target.complete) {
          target.addEventListener('load', () => target.setAttribute('data-lg-in', 'true'), { once: true });
          target.addEventListener('error', () => target.setAttribute('data-lg-in', 'true'), { once: true });
          // Fallback: if load never fires within 1.5s, force reveal
          setTimeout(() => target.setAttribute('data-lg-in', 'true'), 1500);
        } else {
          target.setAttribute('data-lg-in', 'true');
        }
        io.unobserve(target);
      });
    }, { threshold: 0.02, rootMargin: '20% 0px' });
    els.forEach((el) => {
      // If already loaded and in view initially, reveal immediately
      if (el.tagName === 'IMG' && el.complete && el.naturalWidth > 0) {
        el.setAttribute('data-lg-in', 'true');
      } else {
        io.observe(el);
      }
    });
  }

  /* ===== SCROLL PARALLAX ===== */
  function initParallax() {
    if (reduceMotion) return;
    const roots = document.querySelectorAll('[data-lg-parallax-root]');
    if (!roots.length) return;
    const items = [];
    roots.forEach((root) => {
      root.querySelectorAll('[data-lg-parallax-el]').forEach((el) => {
        const speed = parseFloat(el.dataset.lgParallaxSpeed || '0.2');
        items.push({ root, el, speed });
      });
    });
    if (!items.length) return;

    let ticking = false;
    function update() {
      const vh = window.innerHeight;
      items.forEach(({ root, el, speed }) => {
        const rect = root.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        const centre = rect.top + rect.height / 2;
        const delta = centre - vh / 2;
        const y = -delta * speed;
        el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
      });
      ticking = false;
    }
    function onScroll() {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ===== HERO SCROLL FADE ===== */
  function initHeroScrollFade() {
    if (reduceMotion) return;
    const hero = document.querySelector('[data-lg-hero]');
    if (!hero) return;
    const inner = hero.querySelector('.lg-hero__inner');
    if (!inner) return;
    let ticking = false;
    function update() {
      const y = window.scrollY;
      const h = hero.offsetHeight;
      const progress = Math.min(1, Math.max(0, y / (h * 0.85)));
      inner.style.opacity = String(1 - progress * 0.9);
      inner.style.transform = `translate3d(0, ${(progress * 34).toFixed(1)}px, 0)`;
      ticking = false;
    }
    function onScroll() {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  /* ===== MAGNETIC BUTTONS ===== */
  function initMagnetic() {
    if (reduceMotion) return;
    if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    document.querySelectorAll('[data-lg-magnetic]').forEach((el) => {
      if (el.dataset.lgMagInit === '1') return;
      el.dataset.lgMagInit = '1';
      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect();
        const dx = (e.clientX - r.left - r.width / 2) * 0.18;
        const dy = (e.clientY - r.top - r.height / 2) * 0.18;
        el.style.transform = `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px)`;
      });
      el.addEventListener('mouseleave', () => { el.style.transform = ''; });
    });
  }

  /* ===== STICKY HEADER SHADOW ===== */
  function initStickyHeader() {
    const header = document.getElementById('header-group');
    if (!header) return;
    function update() {
      if (window.scrollY > 40) header.setAttribute('data-lg-scrolled', 'true');
      else header.removeAttribute('data-lg-scrolled');
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ===== ANIMATED COUNTERS ===== */
  function initCounters() {
    if (!('IntersectionObserver' in window)) return;
    const els = document.querySelectorAll('[data-lg-count]');
    if (!els.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const end = parseFloat(el.getAttribute('data-lg-count')) || 0;
        const suffix = el.getAttribute('data-lg-count-suffix') || '';
        const dur = 1200;
        const start = performance.now();
        function tick(now) {
          const t = Math.min(1, (now - start) / dur);
          const eased = 1 - Math.pow(1 - t, 3);
          const v = end * eased;
          el.textContent = (end >= 1000 ? Math.round(v).toLocaleString('it-IT') : Math.round(v)) + suffix;
          if (t < 1) requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
        io.unobserve(el);
      });
    }, { threshold: 0.4 });
    els.forEach((el) => io.observe(el));
  }

  /* ===== KEN BURNS on hero image ===== */
  function initKenBurns() {
    if (reduceMotion) return;
    const img = document.querySelector('.lg-hero__bg-img:not(.lg-hero__bg-img--fallback)');
    if (!img) return;
    img.classList.add('lg-kb-active');
  }

  /* ===== HERO VIDEO reveal / failure handling ===== */
  function initHeroVideo() {
    const video = document.querySelector('.lg-hero__bg-video');
    if (!video) return;
    let revealed = false;
    function reveal() {
      if (revealed) return;
      revealed = true;
      video.setAttribute('data-lg-video-ready', 'true');
    }
    function fail() {
      // Hide the video, foto fallback stays visible below
      video.style.display = 'none';
      // Also start Ken Burns on the fallback image
      const fb = document.querySelector('.lg-hero__bg-img--fallback');
      if (fb) fb.classList.add('lg-kb-active');
    }
    // Reveal when video has enough data to play
    if (video.readyState >= 3) {
      reveal();
    } else {
      video.addEventListener('canplay', reveal, { once: true });
      video.addEventListener('playing', reveal, { once: true });
    }
    // On error (all <source> failed), hide the video
    video.addEventListener('error', fail);
    const sources = video.querySelectorAll('source');
    if (sources.length) {
      sources[sources.length - 1].addEventListener('error', fail);
    }
    // Safety timeout: if nothing loads in 6s, hide it
    setTimeout(() => {
      if (!revealed) fail();
    }, 6000);
    // Try play (some browsers block autoplay if not user-initiated)
    const p = video.play();
    if (p && p.catch) p.catch(() => {});
  }

  /* ===== INTRO PRELOADER ===== */
  function initIntro() {
    const intro = document.querySelector('[data-lg-intro]');
    if (!intro) return;
    // Wait for hero image to be at least started
    const heroImg = document.querySelector('.lg-hero__bg-img');
    const timeout = setTimeout(finish, 1600);
    function finish() {
      clearTimeout(timeout);
      intro.setAttribute('data-lg-intro-done', 'true');
      setTimeout(() => { intro.remove(); }, 1400);
    }
    if (heroImg && !heroImg.complete) {
      heroImg.addEventListener('load', () => setTimeout(finish, 400), { once: true });
      heroImg.addEventListener('error', finish, { once: true });
    } else {
      setTimeout(finish, 900);
    }
  }

  /* ===== CUSTOM CURSOR ===== */
  function initCursor() {
    if (reduceMotion) return;
    if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (document.querySelector('.lg-cursor')) return;

    const dot = document.createElement('div');
    dot.className = 'lg-cursor';
    dot.innerHTML = '<span class="lg-cursor__ring"></span><span class="lg-cursor__dot"></span>';
    document.body.appendChild(dot);

    let mx = window.innerWidth / 2, my = window.innerHeight / 2;
    let rx = mx, ry = my;
    let raf = 0;

    function loop() {
      // smooth follow (lerp)
      rx += (mx - rx) * 0.18;
      ry += (my - ry) * 0.18;
      dot.style.transform = `translate3d(${rx}px, ${ry}px, 0)`;
      raf = requestAnimationFrame(loop);
    }
    loop();

    document.addEventListener('mousemove', (e) => {
      mx = e.clientX;
      my = e.clientY;
    }, { passive: true });

    document.addEventListener('mouseleave', () => {
      dot.setAttribute('data-lg-hide', 'true');
    });
    document.addEventListener('mouseenter', () => {
      dot.removeAttribute('data-lg-hide');
    });

    // Hover states on interactive elements
    function updateHoverState(target) {
      if (target.closest('a, button, [role="button"], [data-lg-magnetic], input, textarea, select')) {
        dot.setAttribute('data-lg-hover', 'true');
      } else {
        dot.removeAttribute('data-lg-hover');
      }
    }
    document.addEventListener('mouseover', (e) => updateHoverState(e.target), { passive: true });
  }

  function boot() {
    // initIntro();       // rimosso: intro curtain confondeva l'apertura
    initFades();
    initBlurUp();
    initParallax();
    initHeroScrollFade();
    initMagnetic();
    initStickyHeader();
    initCounters();
    // initKenBurns();    // rimosso: troppo movimento sull'hero
    initHeroVideo();
    // initCursor();      // rimosso: cursor custom disturbava
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
